/**
 * ═══════════════════════════════════════════════════════════════════════════
 * TOOLTIP LABEL COMPONENT - Main Tooltip System Controller
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * COUPLED COMPONENT - Part of 2-file tooltip system
 * ⚠️  Changes here may require updates to TooltipDrawer.tsx
 *
 * RESPONSIBILITY:
 * ✓ State management (tooltip/drawer open/close)
 * ✓ DrawerManager singleton (lines 141-171) - prevents multiple drawers
 * ✓ Device detection (mobile vs desktop)
 * ✓ Content routing (bubble vs drawer vs fullscreen modal)
 * ✓ Rendering TooltipDrawer component (lines 1077-1083)
 *
 * CHILD COMPONENT:
 * 📁 TooltipDrawer.tsx - Renders drawer UI, receives state from here
 * 📍 Line 1078: isOpen={drawerOpen} - State passed to drawer
 * 📍 Line 1079: onClose={() => DrawerManager.close()} - Callback passed to drawer
 *
 * DRAWER MANAGER SINGLETON (lines 141-171):
 * ✓ Ensures only one drawer open at a time
 * ✓ All tooltip instances subscribe to manager
 * ✓ When drawer opens, others auto-close
 *
 * DEBUGGING DRAWER CLOSE:
 * 1. Check DrawerManager.close() method (line 154)
 * 2. Check notifyListeners() fires (line 156)
 * 3. Check subscription callback (lines 440-442)
 * 4. Check setDrawerOpen(false) executes (line 441)
 * 5. Check TooltipDrawer.tsx receives updated isOpen prop
 *
 * ═══════════════════════════════════════════════════════════════════════════
 */

'use client';

import React, { useState, useEffect, useRef, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Info } from '@/lib/icons';
import { TooltipDrawer } from './TooltipDrawer';
import type { DrawerCtaConfig } from '../lib/calculator.types';
import { DrawerCtaPrimary, DrawerCtaSecondary } from './drawer-buttons';

// Global styles for animations and scrollbar (injected once)
if (typeof window !== 'undefined' && !document.getElementById('tooltip-animations')) {
  const style = document.createElement('style');
  style.id = 'tooltip-animations';
  style.innerHTML = `
    @keyframes slideInFromBottom {
      from {
        transform: translateY(100%);
        opacity: 0.8;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }
    
    @keyframes slideOutToBottom {
      from {
        transform: translateY(0);
        opacity: 1;
      }
      to {
        transform: translateY(100%);
        opacity: 0.8;
      }
    }
    
    .tooltip-fullscreen-enter {
      animation: slideInFromBottom 350ms cubic-bezier(0.4, 0, 0.2, 1) forwards;
    }
    
    .tooltip-fullscreen-exit {
      animation: slideOutToBottom 300ms cubic-bezier(0.4, 0, 0.2, 1) forwards;
    }
    
    .pb-safe {
      padding-bottom: env(safe-area-inset-bottom, 20px);
    }
    
    /* Scrollable tooltip styles */
    .scrollable-tooltip {
      overflow-y: auto;
      overflow-x: hidden;
      position: relative;
    }
    
    /* Custom scrollbar for desktop */
    .scrollable-tooltip::-webkit-scrollbar {
      width: 6px;
    }
    
    .scrollable-tooltip::-webkit-scrollbar-track {
      background: rgba(255, 255, 255, 0.1);
      border-radius: 3px;
    }
    
    .scrollable-tooltip::-webkit-scrollbar-thumb {
      background: #ff37a6;
      border-radius: 3px;
    }
    
    .scrollable-tooltip::-webkit-scrollbar-thumb:hover {
      background: #ff5ab8;
    }
    
    /* Firefox scrollbar */
    .scrollable-tooltip {
      scrollbar-width: thin;
      scrollbar-color: #ff37a6 rgba(255, 255, 255, 0.1);
    }
    
    /* Mobile fullscreen scrollbar */
    .mobile-scrollable::-webkit-scrollbar {
      width: 4px;
    }
    
    .mobile-scrollable::-webkit-scrollbar-track {
      background: rgba(255, 255, 255, 0.05);
    }
    
    .mobile-scrollable::-webkit-scrollbar-thumb {
      background: #ff37a6;
      border-radius: 2px;
    }
    
    /* Gradient fade at bottom when scrollable */
    .scroll-fade-bottom::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 20px;
      background: linear-gradient(to bottom, transparent, #1e293b);
      pointer-events: none;
      border-radius: 0 0 6px 6px;
    }
  `;
  document.head.appendChild(style);
}

// Singleton manager to ensure only one tooltip is open at a time
const TooltipManager = {
  currentTooltip: null as string | null,
  listeners: new Set<(id: string | null) => void>(),
  
  open(id: string) {
    if (this.currentTooltip && this.currentTooltip !== id) {
      this.close();
    }
    this.currentTooltip = id;
    this.notifyListeners();
  },
  
  close() {
    this.currentTooltip = null;
    this.notifyListeners();
  },
  
  isOpen(id: string) {
    return this.currentTooltip === id;
  },
  
  subscribe(listener: (id: string | null) => void) {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  },
  
  notifyListeners() {
    this.listeners.forEach(listener => listener(this.currentTooltip));
  }
};

// Singleton manager to ensure only one drawer is open at a time
const DrawerManager = {
  currentDrawer: null as string | null,
  listeners: new Set<(id: string | null) => void>(),

  open(id: string) {
    if (this.currentDrawer && this.currentDrawer !== id) {
      this.close();
    }
    this.currentDrawer = id;
    this.notifyListeners();
  },

  close() {
    this.currentDrawer = null;
    this.notifyListeners();
  },

  isOpen(id: string) {
    return this.currentDrawer === id;
  },

  subscribe(listener: (id: string | null) => void) {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  },

  notifyListeners() {
    this.listeners.forEach(listener => listener(this.currentDrawer));
  }
};

// Content size detection - determines tooltip behavior
const isLargeTooltip = (content: string | ReactNode): boolean => {
  if (typeof content === 'string') {
    const textLength = content.length;
    return textLength > 300;
  }
  // For ReactNode content, default to small tooltip behavior
  return false;
};

// Detect if content needs scrollable treatment on desktop
// DISABLED: Desktop tooltips never use scrollbars - show full content
const needsScrollableTooltip = (content: string | ReactNode): boolean => {
  // Always return false for desktop - no scrollbars needed
  // Content should be sized appropriately to fit without scrolling
  return false;
};

// Device type detection with comprehensive checks
const getDeviceType = () => {
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const width = window.innerWidth;

  const isMobile = hasTouch && width < 768;
  const isTablet = hasTouch && width >= 768 && width < 1024;
  const isDesktop = !hasTouch && width >= 1024; // Updated to 1024px for drawer compatibility
  const isHybrid = hasTouch && width >= 1024; // Desktop with touch

  return {
    isMobile,
    isTablet,
    isDesktop,
    isHybrid,
    shouldUseTouchUI: isMobile || isTablet, // Excludes desktop and hybrid
    shouldShowTapText: hasTouch, // Any touch device shows "tap"
    shouldUseFullScreen: (content: string | ReactNode) => {
      // Use full screen for large tooltips on mobile/tablet/hybrid (any touch device)
      return (isMobile || isTablet || isHybrid) && isLargeTooltip(content);
    }
  };
};

// Full-screen tooltip component for mobile/tablet large content
const FullScreenTooltip: React.FC<{
  content: string | ReactNode;
  onClose: () => void;
  isVisible: boolean;
  ctaConfig?: DrawerCtaConfig;
}> = ({ content, onClose, isVisible, ctaConfig }) => {
  const [isAnimating, setIsAnimating] = React.useState(false);

  // Apply defaults - always show primary CTA (mobile always stacked)
  const finalCtaConfig: DrawerCtaConfig = {
    primary: {
      text: ctaConfig?.primary?.text || "Book a Strategy Call",
      href: ctaConfig?.primary?.href || "/mortgage-lead-generation-demo",
      onClick: ctaConfig?.primary?.onClick
    },
    // Site-wide default (Phase 6a): mirror the desktop drawer — offer the playbook
    // as a secondary path on the mobile fullscreen sheet too.
    secondary: ctaConfig?.secondary ?? {
      text: "Read the Complete Playbook →",
      href: "/guide/mortgage-marketing-strategies"
    },
    layout: 'stacked'  // Mobile always stacks
  };

  // Handle animation timing
  React.useEffect(() => {
    if (isVisible) {
      // Small delay for smooth entrance
      const timer = setTimeout(() => setIsAnimating(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsAnimating(false);
    }
  }, [isVisible]);

  // Handle back button on mobile - close modal before navigating away
  React.useEffect(() => {
    if (!isVisible) return;

    // Push a guard state when modal opens
    if (typeof window !== 'undefined' && window.history?.pushState) {
      window.history.pushState({ tooltipModalOpen: true }, '');

      // Listen for back button
      const handlePopState = (event: PopStateEvent) => {
        // If we detect our guard state being popped, close the modal
        if (event.state === null || event.state?.tooltipModalOpen) {
          onClose();
        }
      };

      window.addEventListener('popstate', handlePopState);

      // Cleanup listener on unmount
      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [isVisible, onClose]);

  // Format content for display
  const formatContent = (text: string) => {
    return text.split('\n').map((line, i) => {
      // Check for no-bold marker
      const hasNoBoldMarker = line.trim().startsWith('~:');
      const cleanLine = hasNoBoldMarker ? line.trim().substring(2) : line;

      // Check if line is a title (ends with colon) - but NOT if it has the no-bold marker
      const isTitle = cleanLine.endsWith(':') && !hasNoBoldMarker;
      // Check for bullet points
      const isBullet = cleanLine.trim().startsWith('•');

      if (!cleanLine.trim()) {
        return <div key={i} className="h-2" />;
      }

      if (isTitle) {
        return (
          <h3 key={i} className="font-semibold text-white text-base mb-2 mt-4 first:mt-0">
            {cleanLine}
          </h3>
        );
      }

      if (isBullet) {
        return (
          <li key={i} className="text-gray-300 text-sm leading-relaxed mb-2 ml-4">
            {cleanLine.replace('•', '').trim()}
          </li>
        );
      }

      return (
        <p key={i} className="text-gray-300 text-sm leading-relaxed mb-2">
          {cleanLine}
        </p>
      );
    });
  };
  
  const handleClose = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onClose();
  };
  
  return (
    <div 
      className={`fixed inset-0 bg-[#1e293b] z-[10000] transform transition-all ease-out`}
      style={{
        transitionDuration: '350ms',
        willChange: 'transform, opacity',
        transform: isAnimating ? 'translateY(0) translateZ(0)' : 'translateY(100%) translateZ(0)',
        opacity: isAnimating ? 1 : 0.95,
      }}
    >
      {/* Header */}
      <div className="sticky top-0 bg-[#1e293b] border-b border-slate-600 px-4 py-3 flex justify-between items-center">
        <h2 className="text-lg font-semibold text-white">Learn More</h2>
        <button
          onClick={handleClose}
          onTouchEnd={handleClose}
          className="p-2 hover:bg-slate-700 active:bg-slate-600 rounded-full transition-colors"
          aria-label="Close"
        >
          <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      {/* Content */}
      <div className="px-6 py-4 pb-20 overflow-y-auto mobile-scrollable" style={{ maxHeight: 'calc(100vh - 120px)' }}>
        <div className="max-w-2xl mx-auto">
          {typeof content === 'string' ? formatContent(content) : content}
        </div>
      </div>
      
      {/* Bottom CTA Buttons - Replaces close button (X in header is sufficient) */}
      <div className="absolute bottom-0 left-0 right-0 p-4 bg-[#1e293b] border-t border-slate-600">
        <div className="space-y-3">
          {finalCtaConfig.primary && (
            <DrawerCtaPrimary
              text={finalCtaConfig.primary.text || "Book a Strategy Call"}
              href={finalCtaConfig.primary.href}
              onClick={finalCtaConfig.primary.onClick}
            />
          )}
          {finalCtaConfig.secondary && (
            <DrawerCtaSecondary
              text={finalCtaConfig.secondary.text}
              href={finalCtaConfig.secondary.href}
              onClick={finalCtaConfig.secondary.onClick}
              icon={finalCtaConfig.secondary.icon}
            />
          )}
        </div>
      </div>
    </div>
  );
};

interface TooltipLabelProps {
  label?: string;
  children?: ReactNode;
  tooltip: string | ReactNode; // Allow formatted content
  className?: string;
  underline?: boolean; // Whether to show the dotted underline (default true for text labels)
  mobileTimeout?: number; // Mobile auto-dismiss timeout in ms (default 10000)
  showCloseButton?: boolean; // Show X close button on mobile (default true)
  preventCutoff?: boolean; // Prevent tooltip from being cut off at viewport edges (default true)
  showIcon?: boolean; // Whether to show the Info icon (default true)
  clickOnly?: boolean; // Disable hover, require click to open (default false)
  forceDrawer?: boolean; // Always use drawer on desktop ≥1024px (default false)
  drawerCta?: DrawerCtaConfig; // Optional CTA buttons for drawer/modal
}

/**
 * TooltipLabel - World-class dynamic tooltip system with intelligent positioning
 * 
 * Architecture Excellence:
 * - Two-phase rendering: Measurement phase + Display phase
 * - Dynamic dimension calculation for pixel-perfect positioning
 * - Zero layout shift with off-screen measurement
 * - Intelligent viewport-aware positioning algorithm
 * - Performance optimized with minimal reflows
 * 
 * Features:
 * - Rich text formatting with title detection (lines ending with : are styled as titles)
 * - Supports both text labels and custom children (buttons, images, etc)
 * - Perforated underline on hover-able text (optional)
 * - Dark tooltip with arrow pointer
 * - Desktop: Show on hover with intelligent placement
 * - Mobile: Show on touch, auto-dismiss after configurable timeout (default 30s)
 * - Mobile close button (X) for quick dismissal
 * - Automatic positioning that ALWAYS prevents viewport cutoff
 * - Preserves original label styling via className prop
 * 
 * Props:
 * - label: Text to display as the label
 * - tooltip: Content to show in tooltip (string or ReactNode)
 * - className: CSS classes to apply to label (preserves original styling)
 * - underline: Show dotted underline on text labels (default: true)
 * - mobileTimeout: Auto-dismiss timeout in ms for mobile (default: 30000)
 * - showCloseButton: Show X close button on mobile (default: true)
 * - preventCutoff: Prevent tooltip from being cut off at viewport edges (default: true)
 * 
 * Formatting:
 * - Lines ending with ':' are rendered as bold titles with underline
 * - Lines starting with '•' are rendered as bullet points
 * - Lines starting with preset names get arrow indicators
 * - Empty lines create paragraph spacing
 * - Use template literals (backticks) to preserve newlines in tooltip content
 * 
 * Technical Implementation:
 * 1. Tooltip requested → Start measurement phase
 * 2. Render invisibly off-screen → Measure actual dimensions
 * 3. Calculate optimal position based on real size
 * 4. Animate into view with smooth transition
 */
export const TooltipLabel: React.FC<TooltipLabelProps> = ({
  label,
  children,
  tooltip,
  className = '',
  underline = true,
  mobileTimeout = 10000,
  showCloseButton = true,
  preventCutoff = true,
  showIcon = true,
  clickOnly = false,
  forceDrawer = false,
  drawerCta
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [mobileTooltipTimer, setMobileTooltipTimer] = useState<NodeJS.Timeout | null>(null);
  const [positionBelow, setPositionBelow] = useState(false);
  const [tooltipDimensions, setTooltipDimensions] = useState({ width: 0, height: 0 });
  const [isMeasuring, setIsMeasuring] = useState(false);
  const [isHoveringTooltip, setIsHoveringTooltip] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const tooltipContentRef = useRef<HTMLDivElement>(null);

  // Touch detection state
  const [touchStartPos, setTouchStartPos] = useState({ x: 0, y: 0 });
  const [touchStartTime, setTouchStartTime] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);

  // Generate unique ID for this tooltip instance
  const tooltipId = useRef(`tooltip-${Math.random().toString(36).substr(2, 9)}`).current;

  // Subscribe to DrawerManager
  useEffect(() => {
    const unsubscribe = DrawerManager.subscribe((currentId) => {
      setDrawerOpen(currentId === tooltipId);
    });
    return unsubscribe;
  }, [tooltipId]);
  
  // Device type detection
  const [deviceType, setDeviceType] = useState(getDeviceType());
  const [shouldUseFullScreen, setShouldUseFullScreen] = useState(false);
  
  // Check if content needs scrollable treatment
  useEffect(() => {
    setIsScrollable(needsScrollableTooltip(tooltip));
  }, [tooltip]);
  
  // Subscribe to singleton manager
  useEffect(() => {
    const unsubscribe = TooltipManager.subscribe((currentId) => {
      if (currentId !== tooltipId && showTooltip) {
        // Another tooltip opened, close this one
        setShowTooltip(false);
        if (mobileTooltipTimer) {
          clearTimeout(mobileTooltipTimer);
          setMobileTooltipTimer(null);
        }
      }
    });
    return unsubscribe;
  }, [tooltipId, showTooltip, mobileTooltipTimer]);
  
  // Update device type on resize
  useEffect(() => {
    const handleResize = () => {
      const newDeviceType = getDeviceType();
      setDeviceType(newDeviceType);
      setShouldUseFullScreen(newDeviceType.shouldUseFullScreen(tooltip));
    };
    
    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [tooltip]);

  // Determine if should use drawer for large content on desktop
  const shouldUseDrawer = React.useMemo(() => {
    if (typeof window === 'undefined') return false;
    // Only use drawer on true desktop (no touch) with width ≥1024px
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const isDesktop = !hasTouch && window.innerWidth >= 1024;
    const contentLength = typeof tooltip === 'string' ? tooltip.length : 0;
    return isDesktop && (forceDrawer || contentLength >= 400);
  }, [tooltip, forceDrawer]);

  // For drawer-enabled tooltips, show "Click to Learn More" on hover instead of full content
  const hoverTooltipContent = shouldUseDrawer && !clickOnly ? "Click to Learn More" : tooltip;

  // Close drawer if window resizes below 1024px or becomes touch device
  useEffect(() => {
    if (drawerOpen && !shouldUseDrawer) {
      DrawerManager.close();
    }
  }, [drawerOpen, shouldUseDrawer]);

  // Close small tooltips on scroll (mobile)
  useEffect(() => {
    if (showTooltip && !shouldUseFullScreen && deviceType.shouldUseTouchUI) {
      const handleScroll = () => {
        setShowTooltip(false);
        TooltipManager.close();
        if (mobileTooltipTimer) {
          clearTimeout(mobileTooltipTimer);
          setMobileTooltipTimer(null);
        }
      };

      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [showTooltip, shouldUseFullScreen, deviceType.shouldUseTouchUI, mobileTooltipTimer]);

  // Measurement phase: When tooltip should show, measure it first
  useEffect(() => {
    if (showTooltip && !isMeasuring && !isVisible) {
      // Start measurement phase only if not already visible
      setIsMeasuring(true);
    } else if (!showTooltip) {
      // Reset when hiding
      setIsMeasuring(false);
      setIsVisible(false);
    }
  }, [showTooltip, isMeasuring, isVisible]);

  // Measure actual tooltip dimensions once rendered
  useEffect(() => {
    if (isMeasuring && tooltipRef.current) {
      // Get actual dimensions
      const rect = tooltipRef.current.getBoundingClientRect();
      setTooltipDimensions({
        width: rect.width,
        height: rect.height
      });
      // After measuring, make visible
      setIsMeasuring(false);
      setIsVisible(true);
    }
  }, [isMeasuring]);
  
  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (mobileTooltipTimer) {
        clearTimeout(mobileTooltipTimer);
      }
    };
  }, [mobileTooltipTimer]);
  
  // Touch detection handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!deviceType.shouldUseTouchUI) return;
    
    const touch = e.touches[0];
    setTouchStartPos({ x: touch.clientX, y: touch.clientY });
    setTouchStartTime(Date.now());
    setIsScrolling(false);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!deviceType.shouldUseTouchUI || isScrolling) return;
    
    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - touchStartPos.x);
    const deltaY = Math.abs(touch.clientY - touchStartPos.y);
    
    // If finger moved >10px, it's a scroll, not a tap
    if (deltaX > 10 || deltaY > 10) {
      setIsScrolling(true);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!deviceType.shouldUseTouchUI || isScrolling) return;
    
    const touchDuration = Date.now() - touchStartTime;
    
    // Only show tooltip if:
    // 1. Not scrolling (finger didn't move >10px)
    // 2. Touch was quick (<500ms) OR deliberate (>500ms for long press)
    
    if (!isScrolling) {
      if (touchDuration < 500) {
        // Quick tap - add 200ms delay to filter accidents
        setTimeout(() => {
          if (!isScrolling) { // Double-check not scrolling
            showTooltipWithTimer();
          }
        }, 200);
      } else if (touchDuration >= 500) {
        // Long press - show immediately
        showTooltipWithTimer();
      }
    }
  };

  const showTooltipWithTimer = () => {
    // Clear any existing timer
    if (mobileTooltipTimer) {
      clearTimeout(mobileTooltipTimer);
    }
    
    // Notify manager that this tooltip is opening
    TooltipManager.open(tooltipId);
    
    setShowTooltip(true);
    
    // Only auto-dismiss for small tooltips
    // Large tooltips must be closed manually
    if (!shouldUseFullScreen) {
      const timer = setTimeout(() => {
        setShowTooltip(false);
        TooltipManager.close();
        setMobileTooltipTimer(null);
      }, mobileTimeout);
      
      setMobileTooltipTimer(timer);
    }
  };
  
  // References for positioning
  const spanRef = useRef<HTMLSpanElement>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  // Calculate tooltip position using actual measured dimensions
  useEffect(() => {
    if (showTooltip && spanRef.current && tooltipDimensions.height > 0) {
      const rect = spanRef.current.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      const viewportHeight = window.innerHeight;
      
      // Use actual measured dimensions
      const tooltipWidth = tooltipDimensions.width || (deviceType.shouldUseTouchUI ? Math.min(window.innerWidth - 20, 340) : 320);
      const tooltipHeight = tooltipDimensions.height;
      
      // Calculate horizontal position (centered on element)
      let left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
      
      // Prevent horizontal overflow
      const minMargin = 10;
      if (left < minMargin) {
        left = minMargin;
      } else if (left + tooltipWidth > window.innerWidth - minMargin) {
        left = window.innerWidth - tooltipWidth - minMargin;
      }
      
      // Calculate vertical position with intelligent placement
      const spaceAbove = rect.top;
      const spaceBelow = viewportHeight - rect.bottom;
      const arrowHeight = 8;
      
      let top;
      let below = false;
      
      if (preventCutoff) {
        // Smart positioning based on actual measured height
        const fitsAbove = spaceAbove >= tooltipHeight + arrowHeight + minMargin;
        const fitsBelow = spaceBelow >= tooltipHeight + arrowHeight + minMargin;
        
        if (fitsAbove && !fitsBelow) {
          // Only fits above
          top = rect.top - arrowHeight;
          below = false;
        } else if (!fitsAbove && fitsBelow) {
          // Only fits below
          top = rect.bottom + arrowHeight;
          below = true;
        } else if (fitsAbove && fitsBelow) {
          // Fits both ways - prefer above for consistency
          top = rect.top - arrowHeight;
          below = false;
        } else {
          // Doesn't fit either way - choose side with more space
          if (spaceAbove > spaceBelow) {
            // More space above - position at top of viewport with scroll
            top = Math.max(minMargin + tooltipHeight, rect.top - arrowHeight);
            below = false;
          } else {
            // More space below
            top = rect.bottom + arrowHeight;
            below = true;
          }
        }
      } else {
        // Simple positioning (legacy behavior)
        top = rect.top - arrowHeight;
        below = false;
      }
      
      setTooltipPosition({ top, left });
      setPositionBelow(below);
    }
  }, [showTooltip, tooltipDimensions, deviceType.shouldUseTouchUI, preventCutoff]);

  // Calculate tooltip position on mobile
  const getMobileTooltipStyles = () => {
    if (!deviceType.shouldUseTouchUI || !spanRef.current) {
      return {};
    }
    
    const rect = spanRef.current.getBoundingClientRect();
    const tooltipWidth = Math.min(window.innerWidth - 20, 340); // Max 340px or screen width - padding
    const spaceOnRight = window.innerWidth - rect.left;
    const spaceOnLeft = rect.left + rect.width;
    
    // If label is on the right side of screen, position tooltip to the left
    if (spaceOnRight < tooltipWidth && spaceOnLeft > spaceOnRight) {
      return {
        left: 'auto',
        right: '0',
        width: `${Math.min(spaceOnLeft - 10, tooltipWidth)}px`
      };
    }
    
    // Otherwise position normally from left
    return {
      left: '0',
      right: 'auto',
      width: `${Math.min(spaceOnRight - 10, tooltipWidth)}px`
    };
  };

  const mobileStyles = getMobileTooltipStyles();

  // Determine what content to render
  const content = children || label;
  const hasTextLabel = !children && label;

  return (
    <span
      ref={spanRef}
      className={`relative inline-block ${clickOnly || shouldUseDrawer ? 'cursor-pointer' : ''} ${className}`}
      onMouseEnter={() => {
        if (!deviceType.shouldUseTouchUI && !clickOnly && !drawerOpen) {
          TooltipManager.open(tooltipId);
          setShowTooltip(true);
        }
      }}
      onMouseLeave={() => {
        if (!deviceType.shouldUseTouchUI && !isHoveringTooltip && !clickOnly) {
          // Don't use delay - let TooltipManager handle switching between tooltips
          setShowTooltip(false);
          TooltipManager.close();
        }
      }}
      onClick={() => {
        if (!deviceType.shouldUseTouchUI) {
          if (shouldUseDrawer) {
            setShowTooltip(false);
            DrawerManager.open(tooltipId);
          } else if (clickOnly) {
            TooltipManager.open(tooltipId);
            setShowTooltip(true); // Open bubble
          }
        } else if (clickOnly) {
          // Mobile: clickOnly tooltips should open on tap
          TooltipManager.open(tooltipId);
          setShowTooltip(true);
        }
      }}
    >
      {hasTextLabel && underline ? (
        // Text label with dotted underline
        <span className="inline-flex items-center gap-1.5">
          <span className={className || "cursor-help p-[2px_4px] -m-[2px_4px]"}>
            <span
              style={{
                borderBottom: '1px dotted #94a3b8',
                paddingBottom: '1px'
              }}
            >
              {label}
            </span>
          </span>
          {showIcon && (
            <Info
              className={`h-4 w-4 text-gray-400 hover:text-blue-500 active:text-blue-500 transition-colors duration-200 ${shouldUseDrawer || clickOnly ? 'cursor-pointer' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                if (deviceType.shouldUseTouchUI) {
                  showTooltipWithTimer();
                } else {
                  if (shouldUseDrawer) {
                    setShowTooltip(false);
                    DrawerManager.open(tooltipId);
                  } else if (clickOnly) {
                    TooltipManager.open(tooltipId);
                    setShowTooltip(true);
                  }
                }
              }}
            />
          )}
        </span>
      ) : hasTextLabel ? (
        // Text label without underline - preserve original className
        <span className="inline-flex items-center gap-1.5">
          <span className={className || "cursor-help"}>
            {label}
          </span>
          {showIcon && (
            <Info
              className={`h-4 w-4 text-gray-400 hover:text-blue-500 active:text-blue-500 transition-colors duration-200 ${shouldUseDrawer || clickOnly ? 'cursor-pointer' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                if (deviceType.shouldUseTouchUI) {
                  showTooltipWithTimer();
                } else {
                  if (shouldUseDrawer) {
                    setShowTooltip(false);
                    DrawerManager.open(tooltipId);
                  } else if (clickOnly) {
                    TooltipManager.open(tooltipId);
                    setShowTooltip(true);
                  }
                }
              }}
            />
          )}
        </span>
      ) : (
        // Custom children
        showIcon ? (
          <span className="inline-flex items-center gap-1.5">
            <span>
              {children}
            </span>
            <Info
              className={`h-4 w-4 text-gray-400 hover:text-blue-500 active:text-blue-500 transition-colors duration-200 ${shouldUseDrawer || clickOnly ? 'cursor-pointer' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                if (deviceType.shouldUseTouchUI) {
                  showTooltipWithTimer();
                } else {
                  if (shouldUseDrawer) {
                    setShowTooltip(false);
                    DrawerManager.open(tooltipId);
                  } else if (clickOnly) {
                    TooltipManager.open(tooltipId);
                    setShowTooltip(true);
                  }
                }
              }}
            />
          </span>
        ) : (
          <>{children}</>
        )
      )}
      {/* Render FULL SCREEN tooltip for large content on mobile/tablet */}
      {shouldUseFullScreen && showTooltip && typeof window !== 'undefined' && createPortal(
        <FullScreenTooltip
          content={tooltip}
          isVisible={showTooltip}
          ctaConfig={drawerCta}
          onClose={() => {
            // Add small delay to prevent re-triggering from bubbled events
            setTimeout(() => {
              setShowTooltip(false);
              TooltipManager.close();
              if (mobileTooltipTimer) {
                clearTimeout(mobileTooltipTimer);
                setMobileTooltipTimer(null);
              }
            }, 50);
          }}
        />,
        document.body
      )}
      {/* Render REGULAR tooltip in a portal to escape overflow containers */}
      {!shouldUseFullScreen && typeof window !== 'undefined' && (showTooltip || isMeasuring) && createPortal(
        <div
          ref={tooltipRef}
          onMouseEnter={() => {
            if (!deviceType.shouldUseTouchUI && isScrollable) {
              setIsHoveringTooltip(true);
            }
          }}
          onMouseLeave={() => {
            if (!deviceType.shouldUseTouchUI && isScrollable) {
              setIsHoveringTooltip(false);
              setShowTooltip(false);
              TooltipManager.close();
            }
          }}
          style={{
            pointerEvents: (deviceType.shouldUseTouchUI && showCloseButton) || (!deviceType.shouldUseTouchUI && isScrollable) ? 'auto' : 'none',
            position: 'fixed',
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            transform: positionBelow 
              ? `translateY(${showTooltip && isVisible ? '0' : '5%'}) scale(${showTooltip && isVisible ? 1 : 0.95})` 
              : `translateY(${showTooltip && isVisible ? '-100%' : '-95%'}) scale(${showTooltip && isVisible ? 1 : 0.95})`,
            padding: '10px 14px',
            paddingRight: deviceType.shouldUseTouchUI && showCloseButton ? '32px' : '14px',
            backgroundColor: '#1e293b',
            color: 'white',
            fontSize: '13px',
            lineHeight: '1.6',
            borderRadius: '6px',
            textAlign: (shouldUseDrawer && !clickOnly) ? 'center' : 'left',
            width: deviceType.shouldUseTouchUI ? mobileStyles.width : 'auto',
            maxWidth: deviceType.shouldUseTouchUI ? 'none' : ((shouldUseDrawer && !clickOnly) ? '180px' : '320px'),
            minWidth: deviceType.shouldUseTouchUI ? 'auto' : ((shouldUseDrawer && !clickOnly) ? 'auto' : '250px'),
            maxHeight: !deviceType.shouldUseTouchUI && isScrollable ? '400px' : 'none',
            zIndex: 9999,
            opacity: showTooltip && isVisible && !isMeasuring ? 1 : 0,
            visibility: isMeasuring ? 'hidden' : (isVisible ? 'visible' : 'hidden'),
            transition: isMeasuring ? 'none' : 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: showTooltip && isVisible ? '0 10px 25px rgba(0, 0, 0, 0.3)' : 'none'
          }}
        >
          {/* Close button for mobile */}
          {deviceType.shouldUseTouchUI && showCloseButton && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setShowTooltip(false);
                if (mobileTooltipTimer) {
                  clearTimeout(mobileTooltipTimer);
                  setMobileTooltipTimer(null);
                }
              }}
              onTouchEnd={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setShowTooltip(false);
                TooltipManager.close();
                if (mobileTooltipTimer) {
                  clearTimeout(mobileTooltipTimer);
                  setMobileTooltipTimer(null);
                }
              }}
              style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                width: '20px',
                height: '20px',
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '50%',
                color: 'white',
                fontSize: '14px',
                lineHeight: '1',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0
              }}
              aria-label="Close tooltip"
            >
              ×
            </button>
          )}
          {typeof hoverTooltipContent === 'string' ? (
            // For string content, split by newlines and render with proper spacing
            <div
              ref={tooltipContentRef}
              className={isScrollable && !deviceType.shouldUseTouchUI ? 'scrollable-tooltip' : ''}
              style={{
                display: 'block',
                maxHeight: isScrollable && !deviceType.shouldUseTouchUI ? '370px' : 'none',
                overflowY: isScrollable && !deviceType.shouldUseTouchUI ? 'auto' : 'visible',
                paddingRight: isScrollable && !deviceType.shouldUseTouchUI ? '8px' : '0',
                position: 'relative'
              }}>
              {hoverTooltipContent.split('\n').map((line, index, array) => {
                const isLastLine = index === array.length - 1;
                // Check if line ends with colon (title formatting)
                if (line.trim().endsWith(':')) {
                  return (
                    <div key={index} style={{ 
                      fontSize: '15px', // 2pt larger than base 13px
                      fontWeight: 'bold',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.3)',
                      paddingBottom: '4px',
                      marginBottom: '8px',
                      marginTop: index === 0 ? '0' : '8px',
                      display: 'block',
                      lineHeight: '1.4'
                    }}>
                      {line}
                    </div>
                  );
                }
                // Check if line starts with bullet point
                if (line.startsWith('•')) {
                  return (
                    <div key={index} style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      marginBottom: '6px',
                      width: '100%' 
                    }}>
                      <span style={{ 
                        marginRight: '8px',
                        flexShrink: 0,
                        display: 'inline-block'
                      }}>•</span>
                      <span style={{ 
                        display: 'inline-block',
                        lineHeight: '1.5'
                      }}>{line.substring(1).trim()}</span>
                    </div>
                  );
                }
                // Check if it's empty line (for paragraph spacing)
                if (line.trim() === '') {
                  return <div key={index} style={{ 
                    height: '12px',
                    display: 'block',
                    width: '100%' 
                  }} />;
                }
                // Check if line starts with preset option (None:, Good:, Great:, Excellent:)
                if (line.trim().match(/^(None:|Good:|Great:|Excellent:)/)) {
                  return (
                    <div key={index} style={{ 
                      display: 'flex',
                      alignItems: 'flex-start',
                      marginTop: '4px',
                      marginBottom: '4px'
                    }}>
                      <span style={{ marginRight: '6px', fontSize: '10px' }}>▸</span>
                      <span>{line}</span>
                    </div>
                  );
                }
                // Regular line (or last line with italic)
                return (
                  <div key={index} style={{
                    marginTop: index === 0 ? '0' : '4px',
                    marginBottom: isLastLine ? '0' : '4px',
                    display: 'block',
                    lineHeight: '1.5',
                    fontStyle: 'normal'
                  }}>
                    {line}
                  </div>
                );
              })}
            </div>
          ) : (
            // For ReactNode content, render as-is
            hoverTooltipContent
          )}
          <span 
            style={{
              position: 'absolute',
              top: positionBelow ? 'auto' : '100%',
              bottom: positionBelow ? '100%' : 'auto',
              left: '50%',
              transform: 'translateX(-50%)',
              marginTop: positionBelow ? '0' : '-1px',
              marginBottom: positionBelow ? '-1px' : '0',
              width: 0,
              height: 0,
              borderLeft: '5px solid transparent',
              borderRight: '5px solid transparent',
              borderTop: positionBelow ? 'none' : '5px solid #1e293b',
              borderBottom: positionBelow ? '5px solid #1e293b' : 'none'
            }}
          />
        </div>,
        document.body
      )}
      {/* Render DRAWER for large content on desktop */}
      {shouldUseDrawer && (
        <TooltipDrawer
          isOpen={drawerOpen}
          onClose={() => DrawerManager.close()}
          content={tooltip}
          title="Learn More"
          ctaConfig={drawerCta}
        />
      )}
    </span>
  );
};

export default TooltipLabel; 
